import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Close, DeleteOutline, VpnLockOutlined } from '@mui/icons-material';
import { QButton, QChip, QIconButton, QTextField, QTextFieldIconWrapper, QTypography } from '@kw/quokka-ui';
import { SnackbarKey, useSnackbar } from 'notistack';
import { KwConfirmationModal } from '../../../kw-ui-components/KwConfirmationModal';
import { useFetchService } from '../../../utils/fetchService';
import { formatCidr, isIpInCidrs, parseCidr } from '../../../utils/ipAllowlist';
import { sendHttpRequest } from '../../../utils/network.service';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { OrganizationSettingCard } from './OrganizationSettingCard';
import {
  OrganizationSettingsDialog,
  OrganizationSettingsFieldLabel,
  OrganizationSettingsSnackbarDismissButton,
  OrganizationSettingsValidationMessage,
} from './OrganizationSettingsDialog';

export const LOGIN_IP_ALLOWLIST_PATH = '/organizations/login-ip-allowlist';
// Matches organization-service's MAX_LOGIN_IP_ALLOWLIST_CIDRS.
export const MAX_ALLOWLIST_ENTRIES = 50;

// `enforce` is also returned but is an internal flag (Engineering only), so the Portal neither shows nor sends it.
interface LoginIpAllowlist {
  cidrs: string[];
  updatedAt: string | null;
  requesterIp: string | null;
}

interface LoginIpAllowlistErrorResponse {
  error?: string;
  invalidEntries?: { index: number; reason: string }[];
}

interface LoginIpAllowlistCardProps {
  isAdmin: boolean;
}

const renderSnackbarDismissAction = (onDismiss: (snackbarId: SnackbarKey) => void, snackbarId: SnackbarKey) => (
  <OrganizationSettingsSnackbarDismissButton aria-label="Dismiss notification" size="small" onClick={() => onDismiss(snackbarId)}>
    <Close fontSize="small" />
  </OrganizationSettingsSnackbarDismissButton>
);

export const LoginIpAllowlistCard = ({ isAdmin }: LoginIpAllowlistCardProps) => {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { data: allowlist, error: loadError, mutate } = useFetchService<LoginIpAllowlist>(LOGIN_IP_ALLOWLIST_PATH, accessToken);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [entries, setEntries] = useState<string[]>([]);
  const [focusRequest, setFocusRequest] = useState<{ row: number } | null>(null);
  const entryInputs = useRef<(HTMLInputElement | null)[]>([]);
  const [serverEntryErrors, setServerEntryErrors] = useState<Record<number, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const requesterIp = allowlist?.requesterIp ?? null;
  const savedCidrs: string[] = allowlist?.cidrs ?? [];

  // Blank rows are ignored. Non-blank rows keep their row index so per-entry server errors can be mapped back.
  const filledRows = entries.flatMap((value, row) => (value.trim() ? [row] : []));
  const pendingCidrs = filledRows.map(row => entries[row].trim());
  const parsedEntries = entries.map(value => (value.trim() ? parseCidr(value) : null));
  const entryErrors = parsedEntries.map((parsed, row) => serverEntryErrors[row] ?? (parsed?.valid === false ? parsed.reason : null));
  const normalizedCidrs = parsedEntries.flatMap(parsed => (parsed?.valid === true ? [parsed.cidr] : []));
  const hasInvalidEntries = entryErrors.some(Boolean);
  const requesterIpAllowed = Boolean(requesterIp) && isIpInCidrs(requesterIp as string, normalizedCidrs);

  let validationError: string | null = null;
  if (normalizedCidrs.length && !hasInvalidEntries && !requesterIpAllowed) {
    const fixHint = filledRows.length >= MAX_ALLOWLIST_ENTRIES ? 'Remove an entry to make room for it' : 'Add it to the list';
    validationError = `Your current IP address (${requesterIp ?? 'unknown'}) is not in the list. ${fixHint}, or you will be locked out.`;
  }

  const isUnchanged = normalizedCidrs.join() === savedCidrs.join();
  const isSaveDisabled = isSaving || hasInvalidEntries || Boolean(validationError) || isUnchanged;

  const openEditor = () => {
    setEntries(savedCidrs.length ? savedCidrs.map(formatCidr) : ['']);
    setFocusRequest(null);
    setServerEntryErrors({});
    setServerError(null);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    if (!isSaving) setIsEditorOpen(false);
  };

  const updateEntry = (row: number, value: string) => {
    setEntries(current => current.map((entry, index) => (index === row ? value : entry)));
    setServerEntryErrors(current => {
      const next = { ...current };
      delete next[row];
      return next;
    });
    setServerError(null);
  };

  const removeEntry = (row: number) => {
    setEntries(current => current.filter((entry, index) => index !== row));
    setServerEntryErrors({});
    setServerError(null);
  };

  // Drops blank rows first so repeated "Add" clicks don't stack empty fields, then focuses the new row.
  const addEntry = (value = '') => {
    const filled = entries.filter(entry => entry.trim());
    if (filled.length >= MAX_ALLOWLIST_ENTRIES) return;
    setEntries([...filled, value]);
    setFocusRequest({ row: filled.length });
    setServerEntryErrors({});
    setServerError(null);
  };

  useEffect(() => {
    if (focusRequest) entryInputs.current[focusRequest.row]?.focus();
  }, [focusRequest]);

  // The entry list scrolls inside the modal: keep the row being edited, including its validation message, in view.
  useEffect(() => {
    const activeInput = entryInputs.current.find(input => input && input === document.activeElement);
    activeInput?.closest('li')?.scrollIntoView?.({ block: 'nearest' });
  }, [entries, serverEntryErrors, focusRequest]);

  const saveAllowlist = async () => {
    setIsSaving(true);
    const result = await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}${LOGIN_IP_ALLOWLIST_PATH}`,
      method: 'PUT',
      body: { cidrs: pendingCidrs },
      successMessage: pendingCidrs.length
        ? 'Login IP allowlist saved.'
        : 'Login IP allowlist removed. Sign-ins are allowed from any network.',
      errorMessage: 'Error saving login IP allowlist.',
      enqueueSnackbar,
      successOptions: {
        action: renderSnackbarDismissAction.bind(null, closeSnackbar),
      },
    });
    setIsSaving(false);

    if (result.error) {
      const { error, invalidEntries = [] } = (result.data || {}) as LoginIpAllowlistErrorResponse;
      setServerEntryErrors(Object.fromEntries(invalidEntries.map(({ index, reason }) => [filledRows[index], reason])));
      setServerError(invalidEntries.length ? null : error || null);
      return;
    }

    setIsEditorOpen(false);
    await mutate();
  };

  const handleSave = () => {
    if (isSaveDisabled) return;
    // Creating a list starts restricting sign-ins, which can lock users out, so it needs an explicit confirmation.
    if (pendingCidrs.length && !savedCidrs.length) {
      setIsConfirmOpen(true);
      return;
    }
    // eslint-disable-next-line no-void -- This save is intentionally started after synchronous validation.
    void saveAllowlist();
  };

  const summary = (() => {
    if (loadError) return <MutedText>The allowlist could not be loaded.</MutedText>;
    if (!allowlist) return <MutedText>Loading…</MutedText>;
    return (
      <SummaryStack>
        {savedCidrs.length ? (
          <ChipList aria-label="Allowed networks">
            {savedCidrs.map(cidr => (
              <li key={cidr}>
                <QChip label={formatCidr(cidr)} size="small" />
              </li>
            ))}
          </ChipList>
        ) : (
          <MutedText>Not configured — sign-ins are allowed from any network.</MutedText>
        )}
        {requesterIp && <CurrentIp>Your current IP address: {requesterIp}</CurrentIp>}
      </SummaryStack>
    );
  })();

  return (
    <>
      <OrganizationSettingCard
        title="Login IP Allowlist"
        description="Restrict which networks can complete a Q-scout Portal sign-in. Organizations with no list are unrestricted. Changes apply to new sign-ins within about a minute."
        icon={<VpnLockOutlined />}
        valueLabel="Allowed networks"
        value={summary}
        lastUpdated={allowlist?.updatedAt ?? undefined}
        onEdit={isAdmin && allowlist ? openEditor : undefined}
      />

      <OrganizationSettingsDialog
        id="edit-login-ip-allowlist"
        open={isEditorOpen}
        title="Edit login IP allowlist"
        body={
          <>
            Only sign-ins from these networks will be allowed. Add single IP addresses (e.g. 203.0.113.10) or CIDR ranges (e.g.
            10.0.0.0/24); IPv4 and IPv6 are supported. Remove every entry to allow sign-ins from any network.
          </>
        }
        primaryLabel="Save"
        onPrimary={handleSave}
        onClose={closeEditor}
        showCancel
        isSaving={isSaving}
        primaryDisabled={isSaveDisabled}
      >
        <CurrentIpRow>
          <span>
            Your current IP address: <strong>{requesterIp ?? 'unknown'}</strong>
          </span>
          {requesterIp && !requesterIpAllowed && (
            <QButton
              variant="outlined"
              size="small"
              onClick={() => addEntry(requesterIp as string)}
              disabled={isSaving || filledRows.length >= MAX_ALLOWLIST_ENTRIES}
            >
              Add my IP
            </QButton>
          )}
        </CurrentIpRow>

        <OrganizationSettingsFieldLabel as="div">IP addresses and ranges</OrganizationSettingsFieldLabel>
        <EntryList>
          {entries.map((value, row) => (
            // eslint-disable-next-line react/no-array-index-key -- rows are positional and have no stable id
            <li key={row}>
              <EntryField
                value={value}
                onChange={event => updateEntry(row, event.target.value)}
                placeholder="203.0.113.10 or 10.0.0.0/24"
                inputProps={{ 'aria-label': `IP address or range ${row + 1}` }}
                inputRef={(input: HTMLInputElement | null) => {
                  entryInputs.current[row] = input;
                }}
                InputProps={{
                  endAdornment: (
                    <QTextFieldIconWrapper position="end">
                      {/* quokka's text-field theme makes adornment buttons absolute (misaligned) and QIconButton is a fixed 20px box, so reset both to keep the icon centred. */}
                      <QIconButton
                        aria-label={`Remove IP address or range ${row + 1}`}
                        sx={{ position: 'static', padding: 0 }}
                        onClick={() => removeEntry(row)}
                        disabled={isSaving}
                      >
                        <DeleteOutline fontSize="small" />
                      </QIconButton>
                    </QTextFieldIconWrapper>
                  ),
                }}
                error={Boolean(entryErrors[row])}
                fullWidth
                disabled={isSaving}
              />
              {entryErrors[row] && (
                <OrganizationSettingsValidationMessage role="alert">{entryErrors[row]}</OrganizationSettingsValidationMessage>
              )}
            </li>
          ))}
        </EntryList>
        <QButton variant="outlined" onClick={() => addEntry()} disabled={isSaving || filledRows.length >= MAX_ALLOWLIST_ENTRIES}>
          Add IP address or range
        </QButton>

        {(validationError || serverError) && (
          <OrganizationSettingsValidationMessage role="alert">{validationError || serverError}</OrganizationSettingsValidationMessage>
        )}
      </OrganizationSettingsDialog>

      {isConfirmOpen && (
        <KwConfirmationModal
          title="Enforce login IP allowlist"
          isOpen
          onClose={() => setIsConfirmOpen(false)}
          description="enforce this allowlist"
          onConfirm={saveAllowlist}
          extraDetails={
            <QTypography variant="bodyRegular">
              Everyone in your organization, including administrators, will be unable to sign in from networks outside this list. If you are
              locked out, contact Quokka Support to restore access.
            </QTypography>
          }
        />
      )}
    </>
  );
};

const SummaryStack = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MutedText = styled('span')`
  color: #57666b;
  font-weight: 400;
`;

const ChipList = styled('ul')`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const CurrentIp = styled('span')`
  color: #57666b;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
`;

const CurrentIpRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f3f5f5;
  font-size: 14px;
  line-height: 20px;
  overflow-wrap: anywhere;
`;

// About six rows; longer lists scroll inside the modal instead of pushing it past the viewport.
const EntryList = styled('ul')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
`;

const EntryField = styled(QTextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
  }
`;
